import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight, ImagePlus, X, ShieldCheck, Phone } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { trackCall, trackQuoteSubmit } from "@/lib/analytics";
import { CONTACT, SERVICE_OPTIONS, BUDGET_OPTIONS } from "@/data/content";

const EMPTY = { name: "", email: "", phone: "", service: SERVICE_OPTIONS[0], suburb: "", budget: "", message: "" };
const MAX_PHOTOS = 8;
const MAX_BYTES = 10 * 1024 * 1024;

const HERO_IMG =
  "https://images.unsplash.com/photo-1701251786408-d0320ecaad8d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const ACCENT_IMG =
  "https://images.unsplash.com/photo-1744025098626-66c0b9cb1ba8?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

const TRUST = ["Fully insured", "15+ years", "Free measure & quote"];

export default function QuoteModal({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Lock background scroll + close on Escape while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter((f) => f.type.startsWith("image/") && f.size <= MAX_BYTES);
    if (valid.length < files.length) toast.error("Some files were skipped (images under 10MB only).");
    setPhotos((prev) => [...prev, ...valid].slice(0, MAX_PHOTOS));
    e.target.value = "";
  };

  const removePhoto = (i) => setPhotos((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      photos.forEach((p) => fd.append("photos", p));
      await api.post("/enquiries", fd);
      trackQuoteSubmit("quote_popup");
      setForm(EMPTY);
      setPhotos([]);
      onClose();
      navigate("/thank-you");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Please call us.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-[#0f172a]/20 focus:border-[#9c7a4d] text-[#0f172a] placeholder:text-[#94a3b8] py-2.5 text-base font-light outline-none transition-colors duration-300";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="quote-modal-overlay"
          data-lenis-prevent
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-stretch sm:items-center justify-center bg-[#100e0b]/70 backdrop-blur-md sm:p-6"
        >
          <motion.div
            data-testid="quote-modal"
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-4xl h-[100dvh] sm:h-auto sm:max-h-[92vh] bg-[#f8fafc] shadow-[0_40px_120px_-40px_rgba(0,0,0,0.7)] flex flex-col md:grid md:grid-cols-5 overflow-hidden"
          >
            <button
              data-testid="quote-modal-close"
              onClick={onClose}
              aria-label="Close"
              className="absolute top-4 right-4 z-20 h-10 w-10 flex items-center justify-center bg-black/40 hover:bg-black/70 text-white backdrop-blur-sm transition-colors duration-300"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Brand / imagery panel */}
            <div className="relative md:col-span-2 bg-[#0b1120] shrink-0 overflow-hidden min-h-[190px] md:min-h-full">
              <img
                src={HERO_IMG}
                alt="Luxury marble tiling"
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover opacity-55"
              />
              <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-b from-[#100e0b]/85 via-[#100e0b]/35 to-transparent" />
              <div className="relative h-full flex flex-col justify-between p-6 md:p-8">
                <img
                  src="/brand/logo.png"
                  alt="Perth Tiling WA"
                  className="h-11 md:h-14 w-auto object-contain drop-shadow-lg"
                />
                <div className="hidden md:block">
                  <div className="mb-5 w-28 h-20 rounded-sm overflow-hidden border border-white/15 shadow-lg">
                    <img src={ACCENT_IMG} alt="Tiling detail" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  </div>
                  <p className="font-serif font-light text-white text-2xl leading-tight">
                    Craftsmanship you can stand on.
                  </p>
                  <p className="text-white/60 text-sm mt-2 font-light">
                    Send a few photos and we'll come back with honest advice and a fair quote.
                  </p>
                </div>
              </div>
            </div>

            {/* Form panel */}
            <div data-lenis-prevent className="relative md:col-span-3 flex-1 overflow-y-auto overscroll-contain p-6 sm:p-8 md:p-10">
              <>
                  <p className="overline !text-[#9c7a4d] mb-2">Free Quote</p>
                  <h3 className="font-serif font-light text-[#0f172a] text-3xl sm:text-4xl leading-[1.05] tracking-tight mb-2">
                    Get your free quote
                  </h3>
                  <p className="text-[#64748b] text-sm font-light mb-5">
                    Tell us about your project — attach photos so we can quote accurately.
                  </p>

                  <div className="flex flex-wrap gap-x-4 gap-y-2 mb-6">
                    {TRUST.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1.5 text-[0.7rem] uppercase tracking-[0.14em] text-[#94a3b8]">
                        <ShieldCheck className="h-3.5 w-3.5 text-[#9c7a4d]" /> {t}
                      </span>
                    ))}
                  </div>

                  <form onSubmit={submit} data-testid="quote-modal-form" className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                    <div>
                      <label className="overline !text-[#94a3b8] block mb-2">Name</label>
                      <input data-testid="quote-name" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputClass} />
                    </div>
                    <div>
                      <label className="overline !text-[#94a3b8] block mb-2">Phone</label>
                      <input data-testid="quote-phone" required value={form.phone} onChange={set("phone")} placeholder="04xx xxx xxx" className={inputClass} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="overline !text-[#94a3b8] block mb-2">Email</label>
                      <input data-testid="quote-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputClass} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="overline !text-[#94a3b8] block mb-2">Service</label>
                      <select data-testid="quote-service" value={form.service} onChange={set("service")} className={`${inputClass} appearance-none cursor-pointer`}>
                        {SERVICE_OPTIONS.map((s) => (
                          <option key={s} value={s} className="bg-white text-[#0f172a]">{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="overline !text-[#94a3b8] block mb-2">Suburb</label>
                      <input data-testid="quote-suburb" value={form.suburb} onChange={set("suburb")} placeholder="e.g. Cottesloe" className={inputClass} />
                    </div>
                    <div>
                      <label className="overline !text-[#94a3b8] block mb-2">Budget</label>
                      <select data-testid="quote-budget" value={form.budget} onChange={set("budget")} className={`${inputClass} appearance-none cursor-pointer`}>
                        <option value="" className="bg-white text-[#0f172a]">Select a range…</option>
                        {BUDGET_OPTIONS.map((b) => (
                          <option key={b} value={b} className="bg-white text-[#0f172a]">{b}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="overline !text-[#94a3b8] block mb-2">Project Details</label>
                      <textarea data-testid="quote-message" required rows={3} value={form.message} onChange={set("message")} placeholder="Tell us about the space, materials and timeline…" className={`${inputClass} resize-none`} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="overline !text-[#94a3b8] block mb-2">Photos (optional)</label>
                      <label
                        data-testid="quote-photos-input"
                        className="flex items-center justify-center gap-3 border border-dashed border-[#0f172a]/25 hover:border-[#9c7a4d] text-[#64748b] hover:text-[#9c7a4d] py-4 cursor-pointer transition-colors"
                      >
                        <ImagePlus className="h-5 w-5" />
                        <span className="text-sm">Add photos of what you'd like done</span>
                        <input type="file" accept="image/*" multiple onChange={addPhotos} className="hidden" />
                      </label>
                      {photos.length > 0 && (
                        <div data-testid="quote-photo-previews" className="mt-3 grid grid-cols-4 gap-2.5">
                          {photos.map((p, i) => (
                            <div key={i} className="relative group aspect-square border border-[#0f172a]/10 overflow-hidden">
                              <img src={URL.createObjectURL(p)} alt={p.name} className="h-full w-full object-cover" />
                              <button
                                type="button"
                                data-testid={`quote-photo-remove-${i}`}
                                onClick={() => removePhoto(i)}
                                className="absolute top-1 right-1 h-5 w-5 flex items-center justify-center bg-black/60 text-white hover:bg-black/80 transition-colors"
                                aria-label="Remove photo"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-[#94a3b8] mt-2">Up to {MAX_PHOTOS} images, 10MB each.</p>
                    </div>
                    <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 mt-1">
                      <button
                        data-testid="quote-submit"
                        type="submit"
                        disabled={loading}
                        className="group inline-flex items-center justify-center gap-3 bg-[#9c7a4d] text-white hover:bg-[#0f172a] disabled:opacity-60 px-8 py-3.5 text-xs uppercase tracking-[0.22em] transition-colors duration-300"
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />}
                        {loading ? "Sending" : "Get My Free Quote"}
                      </button>
                      <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCall("quote_popup")} data-testid="quote-call" className="inline-flex items-center gap-2 text-sm text-[#64748b] hover:text-[#9c7a4d] transition-colors">
                        <Phone className="h-3.5 w-3.5 text-[#9c7a4d]" /> or call {CONTACT.phone}
                      </a>
                    </div>
                  </form>
                </>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
